import { forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from 'react-toastify';

import ZQTable from "./ZQTable";
import { fixFormData } from "./utils";
import GenericForm from "./GenericForm";
import ModuleFilters from "./ModuleFilters";
import { useTranslation } from "react-i18next";

const GenericModule = forwardRef((props, ref) => {
    const {
        classes,
        children,
        module,
        notAdd,
        columnList,
        moduleData,
        validationSchema,
        filterValidationSchema,
        emptyNew,
        setFilters,
        dataList,
        alwayseditable,
        gridEdit,
        refreshData,
        editData,
        insertData,
        deleteData,
        clearLogError,
        onChangeValue,
        childrenPos,
        handleSelEdit,
        handleSelCreate,
        uploadImage,
        previewComplements,
        previewProps,
        firstItemClick,
        canEdit = true,
        canDelete = true,
        additionalOptions,
        setItemsPerPage,
        setPage
    } = props;

    const currentMenuAction = useSelector(state => state.common.currentMenuAction);
    const logguedUserData = useSelector(state => state.common.logguedUserData);

    const [selData, setSelData] = useState(null);
    const [permissions, setPermissions] = useState({});
    const dataRef = useRef();
    const { t } = useTranslation();

    useEffect(() => {
        if (module === currentMenuAction) {
            setPermissions({});
            const selMenu = logguedUserData?.menu?.find(m => m.nombre === currentMenuAction);

            if (selMenu) {
                if (logguedUserData.isAdmin) {
                    setPermissions({ canEdit: true, canDelete: true, canCreate: true })
                } else {
                    const modulepermissions = logguedUserData?.permissions?.find(p => p.modulo_id === selMenu.id && p.conjunto_id === logguedUserData.selConjunto.id);

                    if (modulepermissions) {
                        setPermissions({ canEdit: modulepermissions.editar == 1, canDelete: modulepermissions.borrar == 1, canCreate: modulepermissions.crear == 1 })
                    }
                }
            }
        }
    }, [currentMenuAction, logguedUserData]);

    useEffect(() => {
        if (moduleData.errorMessage) {
            toast(moduleData.errorMessage);
            clearLogError();
        }
    }, [moduleData.errorMessage])

    useImperativeHandle(ref, () => ({
        updateValueFromParent(type, name, value) {
            switch (type) {
                case "selected":
                    dataRef?.current?.updateValueFromParent(name, value);
                    break;
            }
        }
    }));

    const onFilter = (values) => {
        refreshData({ ...values, page: 0, pageCount: 0, rowsPerPage: moduleData.itemsPerPage });
        setFilters(values);
        setPage(0);
    }

    const onSelNew = async () => {
        if (handleSelCreate && typeof handleSelCreate === 'function') {
            handleSelCreate()
        }
        await Promise.all([setSelData(emptyNew || {})]);
        window.$(`#${module}_modal_form`).modal('show');
    }

    const onSelEdit = async (id) => {
        if (handleSelEdit && typeof handleSelEdit === 'function') {
            handleSelEdit(id)
        }
        var selectedElement = dataList.find(x => x.id === id);
        await Promise.all([setSelData(selectedElement)]);
        window.$(`#${module}_modal_form`).modal('show');
    }

    const onSubmitForm = async (values) => {
        const fixedData = fixFormData(columnList, values);
        if (values.id) {
            await editData(fixedData);
        } else {

            await insertData(fixedData);
        }

        const newFilters = { ...moduleData.filters, page: moduleData.page, rowsPerPage: moduleData.itemsPerPage, order: moduleData.order };
        setSelData(fixedData);
        refreshData(newFilters);
        window.$(`#${module}_modal_form`).modal('hide');
    }

    const onDelete = async (id) => {
        const response = await deleteData(id);
        if (response.statusCode && response.statusCode !== 200) {
            toast(`${t('Error deleting item :')} ${response.message}`);
        } else {
            toast(`${t('Deleted item with id:')} ${id}`);
            const newFilters = { ...moduleData.filters, page: moduleData.page, rowsPerPage: moduleData.itemsPerPage, order: moduleData.order };
            refreshData(newFilters);
        }
    }

    const onPageChange = (page) => {
        refreshData({ ...moduleData.filters, page, rowsPerPage: moduleData.itemsPerPage, order: moduleData.order });
        setPage(page);
    }

    const onChangeRowsPerPage = (itemsPerPage) => {
        refreshData({ ...moduleData.filters, page: moduleData.page, rowsPerPage: itemsPerPage, order: moduleData.order });
        setItemsPerPage(itemsPerPage);
    }

    const onChangeOrder = (order) => {
        const newOrder = moduleData.order && moduleData.order === order ? `${order} desc` : order;
        refreshData({ ...moduleData.filters, page: moduleData.page, rowsPerPage: moduleData.itemsPerPage, order: newOrder });
        setFilters({ ...moduleData.filters, order: newOrder });
    }

    return (
        <div className="container-xxl flex-grow-1 container-p-y">
            {
                permissions.canCreate &&
                <div className="buy-now">
                    <div
                        onClick={onSelNew}
                        className="btn btn-buy-now"
                    >+</div>
                </div>
            }

            <ModuleFilters
                columnList={columnList}
                onFilter={onFilter}
                validationSchema={filterValidationSchema}
                prefix={module}
            />
            {
                childrenPos && childrenPos === 'beforeTable' && (children)
            }
            <div className="card">
                <h5 className="card-header personal-module-title">{t(module)}</h5>
                <ZQTable
                    columns={columnList}
                    data={dataList}
                    alwayseditable={alwayseditable}
                    gridEdit={gridEdit}
                    canEdit={canEdit && permissions.canEdit}
                    canDelete={canDelete && permissions.canDelete}
                    isLoading={moduleData.isLoading}
                    onPageChange={onPageChange}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                    orderBy={moduleData.order}
                    onChangeOrder={onChangeOrder}
                    onChangeValue={onChangeValue}
                    onDelete={onDelete}
                    onSelEdit={onSelEdit}
                    prefix={module}
                    additionalOptions={additionalOptions}
                    firstItemClick={firstItemClick}
                    paginationInfo={{
                        page: moduleData.page,
                        count: moduleData.totalItems,
                        rowsPerPage: moduleData.itemsPerPage
                    }}
                />
            </div>
            <div className="row">
                <div className="col-lg-8 mb-4 order-0">
                    {/* {
                        !notAdd &&
                        <Button
                            className={classes.newButton}
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={onSelNew}
                        >
                            + {t(`${'Add'}`)} {t(`${module}_s`)}
                        </Button>
                    } */}
                </div>

            </div>
            <GenericForm
                dataRef={dataRef}
                title={module}
                columnList={columnList}
                selectedData={selData}
                onSubmitForm={onSubmitForm}
                validationSchema={validationSchema}
                uploadImage={uploadImage}
                childrenPos={childrenPos}
                previewComplements={previewComplements?.replace('{moduleFieldId}', selData?.id)}
                previewProps={
                    {
                        icoType: "internalImage",
                        storeId: selData?.store_id,
                        productId: selData?.id || selData?.product_id
                    }
                }
            >
                {
                    childrenPos && childrenPos === 'endForm' && (children)
                }
            </GenericForm>
        </div>
    );
})

export default GenericModule;